import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Play, Download, Lock, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [programRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/programs/${id}`, { headers }),
        token ? axios.get(`${BACKEND_URL}/api/auth/me`, { headers }).catch(() => null) : Promise.resolve(null)
      ]);

      setProgram(programRes.data);
      setUser(userRes?.data);
    } catch (error) {
      toast.error('Error al cargar el programa');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    const token = localStorage.getItem('session_token');
    // Usamos program_id (único) en lugar de level para no colisionar cuando
    // dos programas comparten el mismo level (ej. dos "premium").
    const planParam = program.program_id || program.id || program.level;
    if (!token) {
      toast.error('Debes iniciar sesión primero');
      navigate(`/login?redirect=/checkout?plan=${planParam}`);
      return;
    }

    // Redirigir a la página de checkout
    navigate(`/checkout?plan=${planParam}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Programa no encontrado</h2>
          <Link to="/programs">
            <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full">Volver a Programas</Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasAccess = !program.locked;

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
            {program.level.replace('_', ' ')}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white" style={{fontFamily: 'Syne, sans-serif'}} data-testid="program-detail-title">
            {program.title}
          </h1>
          <p className="text-xl text-zinc-400 mb-6 whitespace-pre-line">{program.description}</p>
          <div className="flex items-center gap-6">
            <div className="text-4xl font-bold text-white" style={{fontFamily: 'Syne, sans-serif'}}>
              ${program.price}
              <span className="text-lg text-zinc-500 font-normal"> pago único</span>
            </div>
            {!hasAccess && (
              <Button 
                onClick={handlePurchase}
                className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-6 font-bold"
                data-testid="purchase-program-button"
              >
                Comprar Programa
              </Button>
            )}
          </div>
        </div>

        {/* Lo que incluye */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-white" style={{fontFamily: 'Syne, sans-serif'}}>Lo que incluye:</h2>
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
            <ul className="space-y-4">
              {program.features?.map((feature, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-pink-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-lg text-zinc-300">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Videos de Entrenamiento por Día */}
          <div className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6">
              <div className="flex items-center mb-6">
                <Calendar className="w-6 h-6 text-pink-500 mr-3" />
                <h3 className="text-2xl font-bold text-white" style={{fontFamily: 'Syne, sans-serif'}}>Rutina de Entrenamiento</h3>
              </div>
              <div className="space-y-3">
                {program.videos?.map((video, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-4 rounded-xl ${hasAccess ? 'bg-zinc-800' : 'bg-zinc-800/50 opacity-60'}`}
                    data-testid={`video-day-${idx + 1}`}
                  >
                    <div className="flex items-center">
                      {hasAccess ? (
                        <Play className="w-5 h-5 text-pink-500 mr-3" />
                      ) : (
                        <Lock className="w-5 h-5 text-zinc-500 mr-3" />
                      )}
                      <span className="font-medium text-white">{video.title}</span>
                    </div>
                    <Button 
                      size="sm" 
                      disabled={!hasAccess}
                      className={hasAccess ? "bg-pink-600 hover:bg-pink-500" : "bg-zinc-700 text-zinc-500 cursor-not-allowed"}
                    >
                      {hasAccess ? 'Ver Video' : <Lock className="w-4 h-4" />}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recursos y Plan Nutricional */}
          <div className="space-y-6">
            {/* Plan Nutricional */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6">
              <h3 className="text-2xl font-bold mb-4 text-white" style={{fontFamily: 'Syne, sans-serif'}}>Plan Nutricional</h3>
              <div className="space-y-3">
                <div className={`p-4 rounded-xl ${hasAccess ? 'bg-zinc-800' : 'bg-zinc-800/50 opacity-60'}`}>
                  <p className="text-zinc-400 mb-4">
                    {program.level === 'nivel_1' && 'Crea tu plan personalizado llenando nuestro formulario'}
                    {program.level === 'nivel_2' && 'Accede a 5 menús diferentes adaptados a tus necesidades'}
                    {program.level === 'nivel_3' && '6 menús especializados: 3 para aumento de masa muscular y 3 para reducción de grasa'}
                  </p>
                  <Button 
                    disabled={!hasAccess}
                    className={hasAccess ? "w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full" : "w-full bg-zinc-700 text-zinc-500 rounded-full cursor-not-allowed"}
                  >
                    {hasAccess ? 'Crear Mi Plan' : <><Lock className="w-4 h-4 mr-2" /> Desbloquear</>}
                  </Button>
                </div>
              </div>
            </div>

            {/* Ebooks */}
            {program.ebooks && program.ebooks.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6">
                <h3 className="text-2xl font-bold mb-4 text-white" style={{fontFamily: 'Syne, sans-serif'}}>Recursos Descargables</h3>
                <div className="space-y-3">
                  {program.ebooks.map((ebook, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-4 rounded-xl ${hasAccess ? 'bg-zinc-800' : 'bg-zinc-800/50 opacity-60'}`}>
                      <span className="text-white">{ebook.title}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        disabled={!hasAccess}
                        className="border-zinc-700 text-zinc-300"
                      >
                        {hasAccess ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Acceso a Academia */}
            <div className="bg-zinc-900 border border-pink-500/30 rounded-3xl p-6">
              <h3 className="text-xl font-bold mb-3 text-white" style={{fontFamily: 'Syne, sans-serif'}}>Biblioteca Educativa</h3>
              <p className="text-zinc-400 mb-4">
                {program.level === 'nivel_1' && 'Acceso a contenido básico de la academia'}
                {program.level === 'nivel_2' && 'Más contenido desbloqueado: Cómo crear rutinas y métodos de entrenamiento'}
                {program.level === 'nivel_3' && 'Acceso completo a todos los videos educativos y métodos avanzados'}
              </p>
              <Link to="/academia">
                <Button className="w-full bg-pink-600/20 border border-pink-500/50 text-pink-400 hover:bg-pink-600 hover:text-white rounded-full">
                  Ver Academia
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        {!hasAccess && (
          <div className="mt-16 bg-gradient-to-r from-pink-600 to-pink-500 text-white rounded-3xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4" style={{fontFamily: 'Syne, sans-serif'}}>
              ¿Lista para comenzar tu transformación?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Obtén acceso de por vida a este programa con un solo pago
            </p>
            <Button 
              onClick={handlePurchase}
              className="bg-white text-pink-600 hover:bg-zinc-100 rounded-full px-12 py-6 text-lg font-bold"
            >
              Comprar por ${program.price}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramDetail;